var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "nativewind/jsx-runtime";
/* eslint-disable no-restricted-imports --
   AddSectionButton 은 편집 모드 전용 도구 — 계측 대상이 아니다.
   (터치 컴포넌트는 Scc* 사용 원칙: .eslintrc.js no-restricted-imports) */
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { View, ScrollView, Text, TouchableOpacity } from 'react-native';
import styled from 'styled-components/native';
import { useNavigation } from '@react-navigation/native';
import { useQuery } from '@tanstack/react-query';
import { api, apiConfig } from '../../config/api';
import { color } from '@/constant/color';
import { getStorageValue } from '@/atoms/atomForLocal';
import { LogParamsProvider } from '@/logging/LogParamsProvider';
import { SccContentTypeDto, UpvoteTargetTypeDto } from '@/generated-sources/openapi';
import { useUpvoteToggle } from '@/hooks/useUpvoteToggle';
import { useSaveContent } from '@/hooks/useSaveContent';
import useAppComponents from '@/hooks/useAppComponents';
import { useAppInjectedAuth } from '../../hooks/useAppInjectedAuth';
import { requestAppLogin } from '@/utils/appWebViewBridge';
import { getIsInAppWebView } from '../../utils/isInAppWebView';
import { getIsLoggedIn } from '../../utils/kakaoAuth';
import HeaderSection from './sections/HeaderSection';
import OverviewSection from './sections/OverviewSection';
import RouteSection from './sections/RouteSection';
import TicketInfoSection from './sections/TicketInfoSection';
import SeatViewSection from './sections/SeatViewSection';
import NearbyPlacesSection from './sections/NearbyPlacesSection';
import ReviewSection from './sections/ReviewSection';
import CTAFooterSection from './sections/CTAFooterSection';
import StickyTabHeader from './components/StickyTabHeader';
import FloatingHeader from './components/FloatingHeader';
import FloatingBottomBar from './components/FloatingBottomBar';
import useSectionNavigation from './hooks/useSectionNavigation';
import { getBbucleRoadConfig, createEmptyBbucleRoadData, } from './config/bbucleRoadData';
import { EditModeProvider, useEditMode } from './context/EditModeContext';
import { ResponsiveProvider, useResponsive } from './context/ResponsiveContext';
import EditSidebar from './edit/EditSidebar';
// Section IDs for navigation
const SECTION_IDS = {
    OVERVIEW: 'overview-section',
    ROUTE: 'route-section',
    TICKET_INFO: 'ticket-info-section',
    SEAT_VIEW: 'seat-view-section',
    NEARBY_PLACES: 'nearby-places-section',
    REVIEW: 'review-section',
};
/**
 * URL에서 editMode queryParam 확인
 */
function getIsEditMode() {
    if (typeof window === 'undefined')
        return false;
    const searchParams = new URLSearchParams(window.location.search);
    return searchParams.get('editMode') === 'true';
}
/**
 * 순수 View 컴포넌트 - data 받아서 그리기만
 */
function BbucleRoadContent({ data, bbucleRoadId }) {
    var _a, _b, _c, _d, _f, _g, _h;
    const { isDesktop } = useResponsive();
    const navigation = useNavigation();
    const handleBackPress = useCallback(() => {
        // 상세의 논리적 부모는 리스트다. 직전 화면이 리스트면 goBack(리스트 스크롤 보존),
        // 아니면 리스트로 이동한다. 로그인 OAuth 풀리로드 후엔 스택이 [Intro, BbucleRoad]
        // 로 재구성돼 goBack 이 home(Intro)으로 가버리므로, 이 경우 리스트로 보낸다.
        const navState = navigation.getState();
        const previousRoute = navState.routes[navState.index - 1];
        if (navigation.canGoBack() && (previousRoute === null || previousRoute === void 0 ? void 0 : previousRoute.name) === 'BbucleRoadList') {
            navigation.goBack();
        }
        else {
            navigation.navigate('BbucleRoadList');
        }
    }, [navigation]);
    const { api: appApi } = useAppComponents();
    // 스크롤 방향 감지 - BottomBar visibility
    const [isBottomBarVisible, setIsBottomBarVisible] = useState(true);
    const lastScrollY = useRef(0);
    const isTouching = useRef(false);
    const isScrollingUpRef = useRef(true);
    const isAtBottomRef = useRef(false);
    const handleScroll = useCallback((event) => {
        var _a, _b;
        const { contentOffset, contentSize, layoutMeasurement } = (_a = event.nativeEvent) !== null && _a !== void 0 ? _a : {};
        const currentScrollY = (_b = contentOffset === null || contentOffset === void 0 ? void 0 : contentOffset.y) !== null && _b !== void 0 ? _b : 0;
        // 최하단 도달 여부 체크 (약간의 여유값 포함)
        const isAtBottom = contentSize && layoutMeasurement &&
            (currentScrollY + layoutMeasurement.height >= contentSize.height - 10);
        isAtBottomRef.current = isAtBottom;
        if (currentScrollY !== lastScrollY.current) {
            isScrollingUpRef.current = currentScrollY < lastScrollY.current;
            lastScrollY.current = currentScrollY;
            // 터치 중이 아닐 때만 상태 업데이트 (관성 스크롤)
            if (!isTouching.current) {
                // 최하단이면 항상 표시, 아니면 스크롤 방향에 따라
                setIsBottomBarVisible(isAtBottom || isScrollingUpRef.current);
            }
        }
    }, []);
    const handleTouchStart = useCallback(() => {
        isTouching.current = true;
    }, []);
    const handleTouchEnd = useCallback(() => {
        isTouching.current = false;
        // 최하단이면 항상 표시, 아니면 스크롤 방향에 따라
        setIsBottomBarVisible(isAtBottomRef.current || isScrollingUpRef.current);
    }, []);
    // 저장된 userId
    const myUserId = useMemo(() => {
        if (typeof window === 'undefined')
            return null;
        return window.localStorage.getItem('bbucleRoadUserId');
    }, []);
    // Upvote 상태 조회
    const { data: upvoteDetails } = useQuery({
        queryKey: ['BbucleRoadUpvoteDetails', bbucleRoadId],
        queryFn: () => __awaiter(this, void 0, void 0, function* () {
            return yield appApi.getUpvoteDetailsPost({
                targetType: UpvoteTargetTypeDto.BbucleRoad,
                id: bbucleRoadId,
            });
        }),
        enabled: !!bbucleRoadId,
    });
    const upvotedUsers = (_b = (_a = upvoteDetails === null || upvoteDetails === void 0 ? void 0 : upvoteDetails.data) === null || _a === void 0 ? void 0 : _a.upvotedUsers) !== null && _b !== void 0 ? _b : [];
    const totalCount = upvotedUsers.length;
    const hasUpvoted = myUserId
        ? upvotedUsers.some((user) => user.userId === myUserId)
        : false;
    // Upvote 토글 hook
    const { isUpvoted, totalUpvoteCount, toggleUpvote } = useUpvoteToggle({
        initialIsUpvoted: hasUpvoted,
        initialTotalCount: totalCount,
        targetId: bbucleRoadId,
        targetType: UpvoteTargetTypeDto.BbucleRoad,
        placeId: '',
    });
    const availableSections = useMemo(() => {
        const sections = [];
        if (data.overviewSection) {
            sections.push({ id: SECTION_IDS.OVERVIEW, label: '한눈에보기' });
        }
        if (data.routeSection) {
            sections.push({ id: SECTION_IDS.ROUTE, label: '교통정보' });
        }
        if (data.ticketInfoSection) {
            sections.push({ id: SECTION_IDS.TICKET_INFO, label: '매표정보' });
        }
        if (data.seatViewSection) {
            sections.push({ id: SECTION_IDS.SEAT_VIEW, label: '시야정보' });
        }
        if (data.nearbyPlacesSection) {
            sections.push({ id: SECTION_IDS.NEARBY_PLACES, label: '근처맛집' });
        }
        if (data.reviewSection) {
            sections.push({ id: SECTION_IDS.REVIEW, label: '방문후기' });
        }
        return sections;
    }, [
        data.overviewSection,
        data.routeSection,
        data.ticketInfoSection,
        data.seatViewSection,
        data.nearbyPlacesSection,
        data.reviewSection,
    ]);
    const sectionIds = useMemo(() => availableSections.map((s) => s.id), [availableSections]);
    const { activeSection, scrollToSection } = useSectionNavigation({ sectionIds });
    // CTA URL - ctaFooterSection의 buttonUrl 또는 기본값 사용
    const ctaButtonUrl = (_d = (_c = data.ctaFooterSection) === null || _c === void 0 ? void 0 : _c.buttonUrl) !== null && _d !== void 0 ? _d : 'https://forms.staircrusher.club/contents-alarm';
    const hasFloatingHeader = !!data.floatingHeaderTitle;
    // CTA 자리에 저장 버튼을 노출할 조건.
    //  - 앱 웹뷰: 주입된 토큰이 있으면 (주입 payload 는 로그아웃 상태에서도 token: null 로
    //    오므로 토큰 유무로 판정). 익명이면 탭 시 앱 로그인 화면으로 위임한다.
    //  - 브라우저: 웹에서 로그인(식별)한 상태. 비로그인/익명은 기존 CTA(정보 요청하기) 유지.
    const appInjectedAuth = useAppInjectedAuth();
    const isInApp = getIsInAppWebView()
        ? !!(appInjectedAuth === null || appInjectedAuth === void 0 ? void 0 : appInjectedAuth.token)
        : getIsLoggedIn();
    // 앱 웹뷰의 익명(비회원) 유저: 좋아요·저장을 익명 계정에 쌓지 않고 앱 로그인 화면으로
    // 위임한다(버튼 노출은 그대로). 브라우저에선 주입이 없어 false — 기존 익명 동작 유지.
    const needsAppLogin = (_f = appInjectedAuth === null || appInjectedAuth === void 0 ? void 0 : appInjectedAuth.isAnonymous) !== null && _f !== void 0 ? _f : false;
    // 저장 상태 조회 — 앱 컨텍스트에서만 의미가 있으므로 token 이 있을 때만 fetch.
    const currentPageUrl = useMemo(() => (typeof window !== 'undefined' ? window.location.href : ''), []);
    const { data: sccContentDetails, isLoading: isSavedStatusLoading } = useQuery({
        queryKey: ['SccContentDetails', currentPageUrl],
        queryFn: () => __awaiter(this, void 0, void 0, function* () { return (yield appApi.getSccContentDetails({ url: currentPageUrl })).data; }),
        enabled: isInApp && currentPageUrl.length > 0,
    });
    const isSaved = (_g = sccContentDetails === null || sccContentDetails === void 0 ? void 0 : sccContentDetails.isSaved) !== null && _g !== void 0 ? _g : false;
    const savedSccContentId = (_h = sccContentDetails === null || sccContentDetails === void 0 ? void 0 : sccContentDetails.sccContentId) !== null && _h !== void 0 ? _h : null;
    // 좋아요도 저장과 동일: 앱 웹뷰의 익명 유저는 앱 로그인으로 위임한다.
    const handleLikePress = useCallback(() => {
        if (needsAppLogin && requestAppLogin())
            return;
        toggleUpvote();
    }, [needsAppLogin, toggleUpvote]);
    const saveContent = useSaveContent();
    const handleToggleSave = useCallback(() => {
        var _a;
        if (!isInApp)
            return;
        // 익명 유저면 앱 로그인 화면으로 위임하고 저장은 중단한다.
        // requestAppLogin() 이 false = 위임 미지원 구버전 앱 → 기존 동작(그대로 저장) 유지.
        if (needsAppLogin && requestAppLogin())
            return;
        saveContent({
            url: currentPageUrl,
            contentType: SccContentTypeDto.WebPage,
            title: (_a = data.title) !== null && _a !== void 0 ? _a : null,
            imageUrls: extractDomImageUrls(),
            description: null,
            currentIsSaved: isSaved,
            currentSccContentId: savedSccContentId,
        });
    }, [
        isInApp,
        needsAppLogin,
        saveContent,
        currentPageUrl,
        data.title,
        isSaved,
        savedSccContentId,
    ]);
    return (_jsxs(LogParamsProvider, { params: { isDesktop }, children: [hasFloatingHeader && (_jsx(FloatingHeader, { title: data.floatingHeaderTitle, onBackPress: handleBackPress })), _jsx(ScrollView, { onScroll: handleScroll, onTouchStart: handleTouchStart, onTouchEnd: handleTouchEnd, scrollEventThrottle: 16, children: _jsxs(ContentWrapper, { hasFloatingHeader: hasFloatingHeader, isDesktop: isDesktop, children: [_jsx(HeaderSection, { titleImageUrl: data.titleImageUrl, mobileTitleImageUrl: data.mobileTitleImageUrl, headerBackgroundImageUrl: data.headerBackgroundImageUrl, mobileHeaderBackgroundImageUrl: data.mobileHeaderBackgroundImageUrl, headerImageCaption: data.headerImageCaption, lastUpdatedDate: data.lastUpdatedDate, wheelchairUserCommentHtml: data.wheelchairUserCommentHtml, wheelchairUserCommentHtmlMobile: data.wheelchairUserCommentHtmlMobile, titleImageWidth: data.titleImageWidth, mobileTitleImageWidth: data.mobileTitleImageWidth, wheelchairUserCommentLabel: data.wheelchairUserCommentLabel, mobileWheelchairUserCommentLabel: data.mobileWheelchairUserCommentLabel }), availableSections.length > 0 && (_jsx(StickyTabHeader, { sections: availableSections, activeSection: activeSection, onTabPress: scrollToSection, topOffset: hasFloatingHeader ? (isDesktop ? 60 : 50) : 0 })), data.overviewSection && (_jsx(OverviewSection, { overviewSection: data.overviewSection, sectionId: SECTION_IDS.OVERVIEW })), data.routeSection && (_jsx(RouteSection, { routeSection: data.routeSection, sectionId: SECTION_IDS.ROUTE })), data.ctaFooterSection && (_jsx(CTAFooterSection, { ctaFooterSection: data.ctaFooterSection, hideCharacter: true })), data.ticketInfoSection && (_jsx(TicketInfoSection, { ticketInfoSection: data.ticketInfoSection, sectionId: SECTION_IDS.TICKET_INFO })), data.seatViewSection && (_jsx(SeatViewSection, { seatViewSection: data.seatViewSection, sectionId: SECTION_IDS.SEAT_VIEW })), data.nearbyPlacesSection && (_jsx(NearbyPlacesSection, { nearbyPlacesSection: data.nearbyPlacesSection, sectionId: SECTION_IDS.NEARBY_PLACES })), data.reviewSection && (_jsx(ReviewSection, { reviewSection: data.reviewSection, sectionId: SECTION_IDS.REVIEW })), data.ctaFooterSection && (_jsx(CTAFooterSection, { ctaFooterSection: data.ctaFooterSection })), _jsx(BottomBarSpacer, { isDesktop: isDesktop })] }) }), _jsx(FloatingBottomBar, { title: data.title, likeCount: totalUpvoteCount !== null && totalUpvoteCount !== void 0 ? totalUpvoteCount : 0, isLiked: isUpvoted, ctaButtonUrl: ctaButtonUrl, onLikePress: handleLikePress, isVisible: isBottomBarVisible, isInApp: isInApp, isSaved: isSaved, isSaveDisabled: isSavedStatusLoading, onSavePress: handleToggleSave })] }));
}
/**
 * 페이지 본문에서 저장에 쓸 이미지 URL 목록을 추출한다.
 * scc-app WebViewScreen 의 OG 추출 스크립트와 같은 규칙:
 * - og:image 를 가장 앞에
 * - 본문 <img src> 를 절대 URL 화하여 추가
 * - data: URI, .svg, 100px 미만 이미지 제외
 * - 등장 순서 유지 + 중복 제거
 */
function extractDomImageUrls() {
    var _a;
    if (typeof document === 'undefined')
        return [];
    const MIN_IMAGE_SIDE = 100;
    const seen = new Set();
    const result = [];
    const ogImage = (_a = document
        .querySelector('meta[property="og:image"]')) === null || _a === void 0 ? void 0 : _a.getAttribute('content');
    if (ogImage) {
        try {
            const abs = new URL(ogImage, document.baseURI).toString();
            if (!seen.has(abs)) {
                seen.add(abs);
                result.push(abs);
            }
        }
        catch (_e) {
            if (!seen.has(ogImage)) {
                seen.add(ogImage);
                result.push(ogImage);
            }
        }
    }
    document.querySelectorAll('img[src]').forEach((img) => {
        const src = img.getAttribute('src');
        if (!src || src.startsWith('data:'))
            return;
        if (src.toLowerCase().includes('.svg'))
            return;
        const imgEl = img;
        const w = imgEl.naturalWidth || 0;
        const h = imgEl.naturalHeight || 0;
        if (w > 0 && h > 0 && (w < MIN_IMAGE_SIDE || h < MIN_IMAGE_SIDE))
            return;
        let abs = src;
        try {
            abs = new URL(src, document.baseURI).toString();
        }
        catch (_e) {
            // 절대 URL 변환 실패 시 원본 src 사용
        }
        if (seen.has(abs))
            return;
        seen.add(abs);
        result.push(abs);
    });
    return result;
}
const SECTION_TEMPLATES = [
    {
        key: 'overviewSection',
        label: '한눈에보기',
        defaultData: {
            title: '근처 정보 한눈에 보기',
            subtitle: '한눈에 보기',
            mapImageUrl: '',
        },
    },
    {
        key: 'routeSection',
        label: '동선정보',
        defaultData: {
            title: '동선정보',
            routes: [
                {
                    id: `route-${Date.now()}`,
                    tabLabel: '새 동선',
                    tabIconType: 'SUBWAY',
                    descriptionImageUrl: '',
                    interactiveImage: { url: '', clickableRegions: [] },
                },
            ],
        },
    },
    {
        key: 'ticketInfoSection',
        label: '매표정보',
        defaultData: {
            title: '매표정보',
            imageUrl: '',
            tips: [],
        },
    },
    {
        key: 'seatViewSection',
        label: '시야정보',
        defaultData: {
            title: '시야정보',
            descriptionHtmls: [],
            interactiveImage: { url: '', clickableRegions: [] },
        },
    },
    {
        key: 'nearbyPlacesSection',
        label: '근처 장소',
        defaultData: {
            title: '근처 장소 정보',
            mapImageUrl: '',
            naverListUrl: 'https://map.naver.com',
            morePlacesUrl: 'https://map.naver.com',
        },
    },
    {
        key: 'reviewSection',
        label: '방문후기',
        defaultData: {
            titleLine1: '장소명',
            titleLine2: '휠체어 이용자의 후기',
            descriptionHtmls: [],
        },
    },
    {
        key: 'ctaFooterSection',
        label: 'CTA 푸터',
        defaultData: {
            buttonUrl: 'https://forms.staircrusher.club/contents-alarm',
        },
    },
];
/**
 * Edit Mode 전용 래퍼
 */
function EditModeContent({ bbucleRoadId }) {
    const editContext = useEditMode();
    // hook 은 early return 앞에서 무조건 호출한다 (react-hooks/rules-of-hooks).
    const updateData = editContext === null || editContext === void 0 ? void 0 : editContext.updateData;
    const handleAddSection = useCallback((key, defaultData) => {
        updateData === null || updateData === void 0 ? void 0 : updateData((prev) => (Object.assign(Object.assign({}, prev), { [key]: defaultData })));
    }, [updateData]);
    if (!editContext)
        return null;
    const { data } = editContext;
    return (_jsxs(EditModeContainer, { children: [_jsxs(MainContent, { children: [_jsx(BbucleRoadContent, { data: data, bbucleRoadId: bbucleRoadId }), SECTION_TEMPLATES.map((template) => !data[template.key] && (_jsx(AddSectionContainer, { children: _jsx(AddSectionButton, { onPress: () => handleAddSection(template.key, template.defaultData), children: _jsxs(AddSectionButtonText, { children: ["+ ", template.label, " \uC139\uC158 \uCD94\uAC00"] }) }) }, template.key)))] }), _jsx(EditSidebar, {})] }));
}
export default function BbucleRoadScreen({ route }) {
    const { bbucleRoadId } = route.params;
    const [isInitializing, setIsInitializing] = useState(true);
    const appInjectedAuth = useAppInjectedAuth();
    const isEditMode = useMemo(() => getIsEditMode(), []);
    // Config에서 데이터 확인
    const configData = useMemo(() => getBbucleRoadConfig(bbucleRoadId), [bbucleRoadId]);
    // Edit Mode용 초기 데이터
    const editModeInitialData = useMemo(() => {
        if (configData)
            return configData;
        return createEmptyBbucleRoadData(bbucleRoadId);
    }, [configData, bbucleRoadId]);
    // 앱이 늦게 토큰을 주입한 경우(atom 로딩 지연 등)에도 즉시 반영한다.
    // 익명 유저로 이미 init 되었더라도 app token + baseUrl 로 덮어쓰면 이후 API 호출은
    // 앱 환경(sandbox/production)의 서버 + 실제 유저로 인증된다.
    useEffect(() => {
        if (appInjectedAuth === null || appInjectedAuth === void 0 ? void 0 : appInjectedAuth.token) {
            apiConfig.accessToken = appInjectedAuth.token;
            if (appInjectedAuth.baseUrl) {
                apiConfig.basePath = appInjectedAuth.baseUrl;
            }
        }
    }, [appInjectedAuth]);
    // Initialize auth (for upvote and image upload)
    useEffect(() => {
        const initializeAuth = () => __awaiter(this, void 0, void 0, function* () {
            try {
                // 0순위: scc-app 웹뷰가 주입한 access token + baseUrl. 익명 유저 생성보다 우선한다.
                if (appInjectedAuth === null || appInjectedAuth === void 0 ? void 0 : appInjectedAuth.token) {
                    apiConfig.accessToken = appInjectedAuth.token;
                    if (appInjectedAuth.baseUrl) {
                        apiConfig.basePath = appInjectedAuth.baseUrl;
                    }
                    // bbucleRoadUserId 가 비어있으면 채워둔다 (좋아요 표시 등에 쓰임)
                    if (!window.localStorage.getItem('bbucleRoadUserId')) {
                        try {
                            const userInfoResponse = yield api.getUserInfoGet();
                            window.localStorage.setItem('bbucleRoadUserId', userInfoResponse.data.user.id);
                        }
                        catch (_a) {
                            // ignore
                        }
                    }
                    setIsInitializing(false);
                    return;
                }
                // 1순위: 메인 앱에 로그인돼 있으면(scc-token) 그 토큰을 그대로 사용한다.
                // 익명 유저를 새로 만들면, 만료된 익명 토큰으로 401 이 날 때 globalAxios 공유
                // 인터셉터가 로그인 유저의 세션까지 풀어버린다. 이미 토큰이 있으면 재사용한다.
                //
                // 단 앱 웹뷰가 "토큰 없음"(로그아웃)을 명시적으로 주입했다면 이 폴백을 건너뛴다.
                // 웹뷰의 localStorage 에는 이전 세션의 scc-token 이 남아 있어(웹뷰 저장소는 앱과
                // 별개) 앱은 로그아웃인데 웹뷰만 로그인 상태로 보이기 때문이다.
                // 주입이 아예 없는 경우(구버전 앱/브라우저)는 기존대로 폴백한다.
                const appLoggedOut = appInjectedAuth !== null && !appInjectedAuth.token;
                const appToken = appLoggedOut
                    ? null
                    : getStorageValue('scc-token');
                if (appToken) {
                    apiConfig.accessToken = appToken;
                    if (!window.localStorage.getItem('bbucleRoadUserId')) {
                        try {
                            const userInfoResponse = yield api.getUserInfoGet();
                            window.localStorage.setItem('bbucleRoadUserId', userInfoResponse.data.user.id);
                        }
                        catch (_b) {
                            // ignore
                        }
                    }
                    setIsInitializing(false);
                    return;
                }
                // Check localStorage for existing token
                const storedToken = window.localStorage.getItem('sccAccessToken');
                if (storedToken) {
                    apiConfig.accessToken = storedToken;
                    setIsInitializing(false);
                    return;
                }
                // Check for anonymous token
                const anonymousToken = window.localStorage.getItem('anonymousAccessToken');
                const tokenExpiry = window.localStorage.getItem('anonymousTokenExpiry');
                if (anonymousToken && tokenExpiry) {
                    const expiryTime = parseInt(tokenExpiry, 10);
                    if (Date.now() < expiryTime) {
                        apiConfig.accessToken = anonymousToken;
                        // userId 없으면 가져오기
                        if (!window.localStorage.getItem('bbucleRoadUserId')) {
                            try {
                                const userInfoResponse = yield api.getUserInfoGet();
                                window.localStorage.setItem('bbucleRoadUserId', userInfoResponse.data.user.id);
                            }
                            catch (_c) {
                                // ignore
                            }
                        }
                        setIsInitializing(false);
                        return;
                    }
                }
                // Create new anonymous login
                const response = yield api.createAnonymousUserPost();
                const { authTokens, userId } = response.data;
                window.localStorage.setItem('anonymousAccessToken', authTokens.accessToken);
                window.localStorage.setItem('anonymousTokenExpiry', String(Date.now() + 10 * 365 * 24 * 60 * 60 * 1000));
                window.localStorage.setItem('bbucleRoadUserId', userId);
                apiConfig.accessToken = authTokens.accessToken;
            }
            catch (error) {
                console.error('Anonymous login failed:', error);
            }
            finally {
                setIsInitializing(false);
            }
        });
        initializeAuth();
    }, []);
    // Edit Mode: Config 데이터 사용
    if (isEditMode) {
        if (isInitializing) {
            return (_jsx(LoadingContainer, { children: _jsx(LoadingText, { children: "\uC778\uC99D \uC911..." }) }));
        }
        return (_jsx(Container, { testID: "bbucle-road-detail", children: _jsx(ResponsiveProvider, { children: _jsx(EditModeProvider, { isEditMode: true, initialData: editModeInitialData, children: _jsx(EditModeContent, { bbucleRoadId: bbucleRoadId }) }) }) }));
    }
    // View Mode: Config 데이터 사용
    if (!configData) {
        return (_jsx(ErrorContainer, { children: _jsx(ErrorText, { children: "\uD398\uC774\uC9C0\uB97C \uCC3E\uC744 \uC218 \uC5C6\uC2B5\uB2C8\uB2E4." }) }));
    }
    if (isInitializing) {
        return (_jsx(LoadingContainer, { children: _jsx(LoadingText, { children: "\uB85C\uB529 \uC911..." }) }));
    }
    return (_jsx(Container, { testID: "bbucle-road-detail", children: _jsx(ResponsiveProvider, { children: _jsx(BbucleRoadContent, { data: configData, bbucleRoadId: bbucleRoadId }) }) }));
}
const Container = styled(View) `
  flex: 1;
  background-color: ${color.white};
`;
const EditModeContainer = styled(View) `
  flex: 1;
  flex-direction: row;
`;
const MainContent = styled(View) `
  flex: 1;
`;
const ContentWrapper = styled(View) `
  width: 100%;
  padding-top: ${({ hasFloatingHeader, isDesktop }) => (hasFloatingHeader ? (isDesktop ? '60px' : '50px') : '0')};
`;
const LoadingContainer = styled(View) `
  flex: 1;
  justify-content: center;
  align-items: center;
  background-color: ${color.white};
`;
const LoadingText = styled(Text) `
  font-size: 18px;
  color: ${color.gray60};
`;
const ErrorContainer = styled(View) `
  flex: 1;
  justify-content: center;
  align-items: center;
  background-color: ${color.white};
  padding: 24px;
`;
const ErrorText = styled(Text) `
  font-size: 16px;
  color: ${color.gray60};
  text-align: center;
`;
const AddSectionContainer = styled(View) `
  padding: 24px 16px;
  align-items: center;
`;
const AddSectionButton = styled(TouchableOpacity) `
  padding: 20px 40px;
  background-color: ${color.iosBlue};
  border-radius: 12px;
`;
const AddSectionButtonText = styled(Text) `
  color: ${color.white};
  font-size: 16px;
  font-weight: 600;
`;
const BottomBarSpacer = styled(View) `
  height: ${({ isDesktop }) => isDesktop ? '97px' : '110px'};
`;
